import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../i18n';
import {
    Tooltip,
    IconButton,
    Typography,
    InputAdornment,
    TextField,
    Box,
    Badge,
    FormControl,
    FormHelperText
} from '@mui/material';
import Iconify from '../../../components/iconify';
import FilterItemsAbstract from '../../../pages/FilterAbstarct/FilterItemsAbstract';
import InputMaskCustom from '../../../components/input-mask/InputMaskCustom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ListIcon from '@mui/icons-material/List';
import ArchiveIcon from '@mui/icons-material/Archive';
// hooks
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    CompareArrows,
    FilterList,
    LockOutlined,
    Search
} from '@mui/icons-material';
import { validateRange } from '../../../utils/dateRangeHelper';
import { StyledSearch } from '../../../components/StyledSearch';
import { StyledRoot } from '../../../components/StyledRoot';
import ToolbarDropdownButton from '../../../components/toolbar/ToolbarDropdownButton';
import DensityControl from './DensityControl';
import ToolbarOverflowMenu from './ToolbarOverflowMenu';

const SelectionInfo = ({ numSelected }) => (
    <Typography component="div" variant="subtitle1">
        {numSelected} selected
    </Typography>
);

const SearchInput = ({
    value,
    onChange,
    onKeyDown,
    handleCloseSearch,
    disabled,
    helperText,
    maxWidth
}) => (
    // A fixed width that does not shrink: as a flex child the field could be
    // squeezed below its own minimum and overlap the filter beside it, and it
    // came out 140 or 177px depending on what else the row held.
    <FormControl sx={{ flexShrink: 0, width: maxWidth || 220 }}>
        <StyledSearch
            size="small"
            value={value}
            disabled={disabled}
            sx={{
                cursor: disabled ? 'not-allowed' : 'text',
                backgroundColor: disabled ? 'background.neutral' : 'inherit'
            }}
            inputProps={{
                title: disabled
                    ? i18n.t('text.searchDisabledWhileFilter')
                    : undefined
            }}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={i18n.t('text.search')}
            startAdornment={
                <InputAdornment position="start">
                    <Iconify
                        icon="eva:search-fill"
                        sx={{ color: 'text.secondary', width: 20, height: 20 }}
                    />
                </InputAdornment>
            }
            endAdornment={
                // Only when there is something to clear: the button was always
                // shown, and clicking it on an empty field did nothing.
                value ? (
                    <IconButton
                        edge="end"
                        size="small"
                        aria-label={i18n.t('common.clearSearch')}
                        onClick={handleCloseSearch}
                    >
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                ) : null
            }
        />
        {/* Rendered unconditionally before, so an empty helper line sat under the
            field on every grid and knocked it out of alignment with its
            neighbours. */}
        {helperText ? (
            <FormHelperText sx={{ marginLeft: 0, color: 'text.secondary' }}>
                {helperText}
            </FormHelperText>
        ) : null}
    </FormControl>
);

const GridListToolbar = ({
    numSelected,
    searchValue,
    handleSearchData,
    handleSearchKeyDown,
    onDelete,
    Head,
    handleFilterItems,
    handleTableSettings,
    onResetColumns,
    NeedSearch,
    onRangeDate,
    onClearRangeDate,
    onSaveRangeDate,
    rangeValue,
    handleOpenDateRange,
    showDatePicker,
    exportButton,
    exportDataToCsv,
    params,
    navigate,
    controller,
    deletePermission,
    filterRequest,
    filterActive,
    disableDateRange,
    disableDateRangeText,
    limitDateRange,
    archivePermission,
    canViewArchived,
    handleArchive,
    toggleArchived,
    archiveActive,
    lockInstance,
    lockInstancePerm,
    transferBalancePerm,
    transferBalance,
    density,
    onDensityChange,
    filterEpoch
}) => {
    const handleCloseSearch = () => {
        if (!searchValue) return;
        handleSearchData('');
    };
    const handleToggleArchived = () => toggleArchived(!archiveActive);

    const mobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const renderRangeDateField = () => {
        if (!showDatePicker) {
            return <></>;
        }

        const isValidRange = validateRange(rangeValue, limitDateRange);
        // const DATE_RANGE_REGEX =
        //     /^\d{2}\/\d{2}\/\d{4}-\d{2}\/\d{2}\/\d{4}$/;
        return (
            <TextField
                id="date"
                size="small"
                label={i18n.t('text.dateRange')}
                type="text"
                name="date"
                placeholder={i18n.t('text.dateRangePlaceholder')}
                value={rangeValue}
                disabled={disableDateRange}
                helperText={
                    disableDateRangeText ||
                    (rangeValue && !isValidRange
                        ? i18n.t('text.dateRangeMax', {
                            count: limitDateRange
                        })
                        : null)
                }
                error={!!rangeValue && !isValidRange}
                variant="outlined"
                onChange={(e) => {
                    // if (DATE_RANGE_REGEX.test(rangeValue)) {
                    //     onSaveRangeDate(rangeValue);
                    // }
                    onRangeDate(e.target.value);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (!isValidRange) {
                            e.preventDefault();
                            return;
                        }
                        onSaveRangeDate(rangeValue);
                    } else if (e.key === 'Escape') {
                        onClearRangeDate();
                    }
                }}
                sx={{ width: '100%', maxWidth: 260, minWidth: 150 }}
                InputProps={{
                    inputComponent: InputMaskCustom,
                    endAdornment: !disableDateRange && (
                        <InputAdornment position="end">
                            {rangeValue && rangeValue.length > 0 ? (
                                <>
                                    <IconButton
                                        onClick={() => onClearRangeDate()}
                                    >
                                        <Iconify icon="eva:close-fill" />
                                    </IconButton>
                                </>
                            ) : (
                                <></>
                            )}
                            <IconButton onClick={handleOpenDateRange}>
                                <Iconify icon="eva:calendar-fill" />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
        );
    };

    const renderExportButton = () => {
        if (!exportButton) {
            return <></>;
        }
        return (
            <Tooltip title={i18n.t('buttons.exportCsv')}>
                <ToolbarDropdownButton
                    iconOnly
                    aria-label={i18n.t('buttons.exportCsv')}
                    icon={<FileDownloadIcon />}
                    onClick={exportDataToCsv}
                />
            </Tooltip>
        );
    };

    const renderSearch = () => {
        return mobileView ? (
            openSearch ? (
                <SearchInput
                    value={searchValue}
                    // helperText={' '}
                    // disabled={!!filterActive}
                    onChange={handleSearchData}
                    onKeyDown={handleSearchKeyDown}
                    handleCloseSearch={handleCloseSearch}
                />
            ) : (
                <ToolbarDropdownButton
                    iconOnly
                    aria-label={i18n.t('text.search')}
                    icon={<Search />}
                    onClick={() => {
                        setOpenSearch(true);
                        setOpenFilter(false);
                    }}
                />
            )
        ) : (
            <SearchInput
                value={searchValue}
                // helperText={filterActive ? i18n.t('text.searchHint') : null}
                // disabled={!!filterActive}
                onChange={handleSearchData}
                onKeyDown={handleSearchKeyDown}
                handleCloseSearch={handleCloseSearch}
            />
        );
    };

    const renderFilter = () => {
        // On a phone the filters hide behind an icon. A grid with no
        // filterable column showed the icon anyway, and it opened nothing.
        if (!(Head || []).some((field) => field.filterCell)) {
            return null;
        }
        return mobileView ? (
            openFilter ? (
                <FilterItemsAbstract
                    // Remount when a chip clears a filter, so the controls
                    // re-read what is left instead of showing a stale value.
                    key={filterEpoch}
                    Head={Head}
                    handleFilterItems={handleFilterItems}
                    params={params}
                    navigate={navigate}
                    controller={controller}
                    filters={filterRequest}
                    // disabled={!!searchValue}
                    // helperText={' '}
                    maxWidth={250}
                />
            ) : (
                <ToolbarDropdownButton
                    iconOnly
                    aria-label={i18n.t('common.filters')}
                    icon={<FilterList />}
                    active={!!filterRequest?.length}
                    onClick={() => {
                        setOpenFilter(true);
                        setOpenSearch(false);
                    }}
                />
            )
        ) : (
            <FilterItemsAbstract
                key={filterEpoch}
                Head={Head}
                handleFilterItems={handleFilterItems}
                params={params}
                navigate={navigate}
                controller={controller}
                filters={filterRequest}
                // disabled={!!searchValue}
                // helperText={searchValue ? i18n.t('text.filterHint') : null}
            />
        );
    };

    const [openSearch, setOpenSearch] = React.useState(false);
    const [openFilter, setOpenFilter] = React.useState(false);

    const overflowItems = [
        // Gated on the column existing, not on permission to archive: archived
        // rows are hidden by default now, so anyone who can read the grid needs
        // this to see them. Archiving a row is the separate, permissioned action
        // below.
        canViewArchived && {
            key: 'archive-toggle',
            label: archiveActive
                ? i18n.t('common.showActive')
                : i18n.t('common.showArchived'),
            icon: archiveActive ? <ListIcon /> : <ArchiveIcon />,
            onClick: handleToggleArchived
        },
        lockInstancePerm && {
            key: 'lock-instance',
            label: i18n.t('common.lockInstance'),
            icon: <LockOutlined />,
            onClick: lockInstance,
            dividerBefore: true
        },
        transferBalancePerm && {
            key: 'transfer-balance',
            label: i18n.t('common.transferBalance'),
            icon: <CompareArrows />,
            onClick: transferBalance
        },
        // Only while some column was sized or moved: then there is a layout
        // to undo, and the grid passes the handler.
        onResetColumns && {
            key: 'reset-columns',
            label: i18n.t('common.resetColumnLayout'),
            icon: <Iconify icon="eva:refresh-outline" width={24} />,
            onClick: onResetColumns,
            dividerBefore: true
        }
    ];

    return (
        <StyledRoot
            sx={{
                // Seven 44px controls are wider than a phone: below md the
                // row wraps instead of clipping the last of them.
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                // A primary tint rather than primary.lighter, which stays
                // pale in dark mode and would put light text on light.
                ...(numSelected > 0 && {
                    color: 'primary.softText',
                    bgcolor: 'primary.tint12'
                })
            }}
        >
            {numSelected > 0 ? (
                <SelectionInfo numSelected={numSelected} />
            ) : (
                <>
                    <Box
                        display="flex"
                        justifyContent="start"
                        alignItems="center"
                        width={{ xs: 'auto', md: '100%' }}
                        gap={1}
                    >
                        {NeedSearch && renderSearch()}
                        {renderFilter()}
                        {/* A date range narrows which rows you see, exactly like
                            the search box and the filter selects. It sat with
                            density and column settings, which only change how
                            you see them. */}
                        {!mobileView &&
                            showDatePicker &&
                            renderRangeDateField()}
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="end"
                        alignItems="center"
                        flexShrink={0}
                        flexWrap="wrap"
                        gap={1}
                    >
                        {mobileView && showDatePicker && (
                            <ToolbarDropdownButton
                                iconOnly
                                aria-label={i18n.t('text.dateRange')}
                                onClick={handleOpenDateRange}
                                disabled={disableDateRange}
                                active={!!rangeValue}
                                icon={
                                    <Badge
                                        color="primary"
                                        variant="dot"
                                        invisible={
                                            !rangeValue ||
                                            rangeValue.length === 0
                                        }
                                    >
                                        <Iconify icon={'eva:calendar-outline'} />
                                    </Badge>
                                }
                            />
                        )}

                        {/* How the table is presented: used constantly, so
                            these stay on the surface. */}
                        {onDensityChange && (
                            <DensityControl
                                density={density}
                                onChange={onDensityChange}
                            />
                        )}
                        <Tooltip title={i18n.t('common.tableSettings')}>
                            <ToolbarDropdownButton
                                iconOnly
                                aria-label={i18n.t('common.tableSettings')}
                                icon={<Iconify icon="eva:settings-2-fill" />}
                                onClick={handleTableSettings}
                            />
                        </Tooltip>
                        {renderExportButton()}

                        {/* Everything else — rare, and in two cases acting on
                            the whole instance rather than the view. */}
                        <ToolbarOverflowMenu items={overflowItems} />
                    </Box>
                </>
            )}

            {numSelected > 0 && (
                <Box display="flex" gap={1}>
                    {archivePermission && (
                        <Tooltip title={i18n.t('buttons.archive')}>
                            <ToolbarDropdownButton
                                iconOnly
                                aria-label={i18n.t('buttons.archive')}
                                icon={<Iconify icon="eva:archive-fill" />}
                                onClick={handleArchive}
                            />
                        </Tooltip>
                    )}
                    {deletePermission && (
                        <Tooltip title={i18n.t('buttons.delete')}>
                            <ToolbarDropdownButton
                                iconOnly
                                aria-label={i18n.t('buttons.delete')}
                                icon={<Iconify icon="eva:trash-2-fill" />}
                                onClick={onDelete}
                            />
                        </Tooltip>
                    )}
                </Box>
            )}
        </StyledRoot>
    );
};

GridListToolbar.propTypes = {
    numSelected: PropTypes.number,
    searchValue: PropTypes.string,
    handleSearchData: PropTypes.func,
    onDelete: PropTypes.func,
    Head: PropTypes.array,
    handleFilterItems: PropTypes.func,
    handleTableSettings: PropTypes.func,
    /** Resets the user's column widths and order; absent when there are none. */
    onResetColumns: PropTypes.func,
    NeedSearch: PropTypes.bool,
    clientId: PropTypes.string,
    handleOpenDateRange: PropTypes.func,
    onRangeDate: PropTypes.func,
    onClearRangeDate: PropTypes.func,
    onSaveRangeDate: PropTypes.func,
    rangeValue: PropTypes.string,
    showDatePicker: PropTypes.bool,
    exportButton: PropTypes.bool,
    exportDataToCsv: PropTypes.func,
    deletePermission: PropTypes.bool,
    /** May archive a row — gates the archive action. */
    archivePermission: PropTypes.bool,
    /** Grid has an isArchived column — gates the show archived/active toggle. */
    canViewArchived: PropTypes.bool,
    toggleArchived: PropTypes.func,
    /** The grid is listing archived rows instead of active ones. */
    archiveActive: PropTypes.bool
};

export default GridListToolbar;
